import React, { useState, useEffect } from 'react';
import { FaTag, FaUser, FaTimes, FaUndo, FaEdit } from 'react-icons/fa';
import CharacterTagOverlay from './CharacterTagOverlay';
import CustomModal from './CustomModal';
import { API_ENDPOINTS } from '../config/backendConfig';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";

const RefineArtworkModal = ({ 
  isOpen, 
  onClose, 
  artworkUrl, 
  onArtworkRefined,
  currentUser,
  taggedCharacters = [],
  storyId
}) => {
  const [refineInstructions, setRefineInstructions] = useState('');
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [artworkImage, setArtworkImage] = useState(null);
  const [originalArtworkUrl, setOriginalArtworkUrl] = useState(artworkUrl);
  const [refinedArtworkUrl, setRefinedArtworkUrl] = useState(null);
  const [showUndoEdit, setShowUndoEdit] = useState(false);

  // Function to crop character image from artwork
  const cropCharacterImage = (artworkImg, tag, cropSize = 256) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = cropSize;
      canvas.height = cropSize;
      const ctx = canvas.getContext('2d');
      
      // Calculate crop position (center on tag)
      const x = (tag.x / 100) * artworkImg.naturalWidth - cropSize / 2;
      const y = (tag.y / 100) * artworkImg.naturalHeight - cropSize / 2;
      
      // Ensure crop stays within image bounds
      const clampedX = Math.max(0, Math.min(x, artworkImg.naturalWidth - cropSize));
      const clampedY = Math.max(0, Math.min(y, artworkImg.naturalHeight - cropSize));
      
      ctx.drawImage(artworkImg, clampedX, clampedY, cropSize, cropSize, 0, 0, cropSize, cropSize);
      
      canvas.toBlob(resolve, 'image/jpeg', 0.95);
    });
  };

  // Function to upload character image to Firebase Storage
  const uploadCharacterImage = async (blob, characterId) => {
    const storage = getStorage();
    const userId = currentUser?.uid || 'anonymous';
    const storageRef = ref(storage, `users/${userId}/characters/${characterId}.jpg`);
    await uploadBytes(storageRef, blob);
    return await getDownloadURL(storageRef);
  };

  // Function to save character image URL to Firestore
  const saveCharacterImageUrl = async (characterId, imageUrl) => {
    // Use initialized Firestore instance from app config to avoid IndexedDB default
    const { db } = await import('../firebase');
    await setDoc(doc(db, "characters", characterId), {
      imageUrl: imageUrl,
      lastUpdated: new Date()
    }, { merge: true });
  };

  // Function to process all tagged characters and save their images
  const processCharacterImages = async (characters) => {
    if (!artworkImage || characters.length === 0) return characters;

    console.log('=== PROCESSING CHARACTER IMAGES FOR REFINEMENT ===');
    console.log('Characters to process:', characters.length);

    const processedCharacters = [];

    for (const character of characters) {
      try {
        // Crop the character image from the artwork
        const croppedBlob = await cropCharacterImage(artworkImage, character);
        
        // Upload to Firebase Storage
        const imageUrl = await uploadCharacterImage(croppedBlob, character.id);
        
        // Save image URL to Firestore
        await saveCharacterImageUrl(character.id, imageUrl);
        
        // Add image URL to character data
        const processedCharacter = {
          ...character,
          imageUrl: imageUrl
        };
        
        processedCharacters.push(processedCharacter);
        console.log(`Processed character: ${character.name}, Image URL: ${imageUrl}`);
        
      } catch (error) {
        console.error(`Error processing character ${character.name}:`, error);
        // Still include the character without image URL
        processedCharacters.push(character);
      }
    }

    console.log('Character image processing complete for refinement');
    return processedCharacters;
  };

  const handleRefineArtwork = async () => {
    if (!refineInstructions.trim()) {
      setModal({
        isOpen: true,
        title: "✏️ Refinement Instructions Required",
        message: "Please describe how you want to refine your artwork! ✨",
        type: 'warning'
      });
      return;
    }

    // Check if the artwork URL is a blob URL (which the backend can't access)
    if (originalArtworkUrl && originalArtworkUrl.startsWith('blob:')) {
      setModal({
        isOpen: true,
        title: "❌ Cannot Refine Artwork",
        message: "The current artwork is not saved yet. Please upload and save your artwork before refining it.",
        type: 'error'
      });
      return;
    }

    setRefining(true);
    setRefineError(null);

    try {
      // Process character images before refining
      const processedCharacters = await processCharacterImages(taggedCharacters);
      console.log('DEBUG: Processed characters for refinement:', processedCharacters);
      
      // Call backend to refine the artwork
      const response = await fetch(API_ENDPOINTS.refineArtwork, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: originalArtworkUrl,
          refineInstructions: refineInstructions,
          artistName: currentUser?.displayName || 'Young Artist',
          userId: currentUser?.uid || 'anonymous',
          storyId: storyId,
          characterIds: processedCharacters.map(char => char.id), // Pass character IDs for image combination
          taggedCharacters: processedCharacters
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to refine artwork');
      }
      
      const data = await response.json();
      
      // Update the refined artwork URL
      setRefinedArtworkUrl(data.refinedImageUrl);
      setShowUndoEdit(true);
      
      // Call the callback to update the parent component
      onArtworkRefined(data.refinedImageUrl);
      
      setModal({
        isOpen: true,
        title: "🎨 Artwork Refined!",
        message: "Your artwork has been successfully refined with Artmosphere's AI magic! ✨",
        type: 'success'
      });
      
    } catch (err) {
      setRefineError(err.message);
      setModal({
        isOpen: true,
        title: "❌ Refinement Failed",
        message: `Failed to refine artwork: ${err.message}`,
        type: 'error'
      });
    } finally {
      setRefining(false);
    }
  };

  const handleUndo = () => {
    setRefinedArtworkUrl(null);
    setShowUndoEdit(false);
    onArtworkRefined(originalArtworkUrl); // Restore original artwork
    setModal({
      isOpen: true,
      title: "↩️ Artwork Restored",
      message: "Your original artwork has been restored! ✨",
      type: 'info'
    });
  };

  const handleEdit = () => {
    // Open the edit modal (this will be handled by the parent component)
    onClose(); // Close refine modal first
    // The parent component should handle opening the edit modal
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setOriginalArtworkUrl(artworkUrl);
      setRefinedArtworkUrl(null);
      setShowUndoEdit(false);
      setRefineInstructions('');
      setRefineError(null);
    }
  }, [isOpen, artworkUrl]);

  if (!isOpen) return null;

  const currentArtworkUrl = refinedArtworkUrl || originalArtworkUrl;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-amber-200">
            <h3 className="text-xl font-bold text-amber-800">🎨 Refine Your Artwork</h3>
            <button
              className="text-gray-500 hover:text-gray-700 text-2xl"
              onClick={onClose}
              title="Close"
            >
              ×
            </button>
          </div>
          
          <div className="flex flex-col lg:flex-row h-full" style={{ height: 'calc(95vh - 100px)' }}>
            {/* Left: Artwork with tags */}
            <div className="lg:w-1/2 p-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-amber-800 mb-2">
                  🎨 Your Artwork
                </h4>
                <p className="text-sm text-amber-600">
                  Your artwork with character tags. The AI will use these tags to maintain character consistency.
                </p>
              </div>
              
              {/* Undo/Edit buttons if artwork has been refined */}
              {showUndoEdit && (
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={handleUndo}
                    className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-xl shadow transition-all duration-300"
                  >
                    <FaUndo /> Undo
                  </button>
                  <button
                    onClick={handleEdit}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-xl shadow transition-all duration-300"
                  >
                    <FaEdit /> Edit
                  </button>
                </div>
              )}
              
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl overflow-hidden relative" style={{ aspectRatio: '1', maxHeight: '60vh' }}>
                <img
                  src={currentArtworkUrl}
                  alt="Your Artwork"
                  className="w-full h-full object-contain"
                  onLoad={(e) => {
                    setArtworkImage(e.target);
                    console.log('Artwork image loaded, ready for character cropping');
                  }}
                  onError={(e) => {
                    console.log('Artwork failed to load in modal');
                    e.target.style.display = 'none';
                  }}
                />
                {/* Character Tag Overlay - Show tags if they exist */}
                <CharacterTagOverlay
                  artworkUrl={currentArtworkUrl}
                  taggedCharacters={taggedCharacters}
                  showTags={true}
                  draggable={false} // Don't allow dragging in refine modal
                />
              </div>
            </div>
            
            {/* Right: Refinement form */}
            <div className="lg:w-1/2 p-6 bg-amber-50 border-l border-amber-200 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 100px)' }}>
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-amber-800 mb-2">
                  ✨ Refine Your Artwork
                </h4>
                <p className="text-sm text-amber-600">
                  Describe how you want to improve your artwork. The AI will maintain character consistency using your tags!
                </p>
              </div>

              {/* Character tags info */}
              {taggedCharacters.length > 0 && (
                <div className="mb-6 p-4 bg-white rounded-xl border-2 border-amber-200">
                  <h5 className="font-semibold text-amber-800 mb-2">
                    🏷️ Character Tags ({taggedCharacters.length})
                  </h5>
                  <div className="space-y-2">
                    {taggedCharacters.map((char, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <FaTag className="text-amber-500" />
                        <span className="font-medium">{char.name}</span>
                        {char.age && <span className="text-gray-600">({char.age})</span>}
                        {char.appearance && <span className="text-gray-600">- {char.appearance}</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    These characters will be preserved in the refined artwork for consistency.
                  </p>
                </div>
              )}

              {/* Refinement instructions */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-amber-800 mb-2">
                  ✍️ How would you like to refine your artwork?
                </label>
                <textarea
                  className="w-full border rounded p-3 min-h-[120px] resize-none"
                  value={refineInstructions}
                  onChange={e => setRefineInstructions(e.target.value)}
                  placeholder="Describe what you want to improve, change, or enhance in your artwork. For example: 'Make the colors more vibrant', 'Add more details to the background', 'Improve the lighting', etc."
                />
              </div>

              {/* Examples */}
              <div className="mb-6 p-4 bg-white rounded-xl border-2 border-amber-200">
                <h5 className="font-semibold text-amber-800 mb-2">
                  💡 Refinement Ideas
                </h5>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• "Make the colors more vibrant and warm"</li>
                  <li>• "Add more details to the background"</li>
                  <li>• "Improve the lighting and shadows"</li>
                  <li>• "Make the characters more expressive"</li>
                  <li>• "Add more texture and depth"</li>
                  <li>• "Enhance the overall composition"</li>
                </ul>
              </div>

              {/* Refine button */}
              <div className="mt-6 pb-4">
                <button
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-400 text-white font-semibold py-3 px-4 rounded-xl shadow hover:from-amber-600 hover:to-amber-500 transition-all duration-300"
                  onClick={handleRefineArtwork}
                  disabled={refining || !refineInstructions.trim()}
                >
                  {refining ? 'Refining Artwork...' : '✨ Refine Artwork'}
                </button>
                
                {refineError && (
                  <div className="text-red-600 text-sm mt-2">{refineError}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Modal for messages */}
      <CustomModal
        isOpen={modal.isOpen}
        onClose={() => {
          setModal({ isOpen: false, title: '', message: '', type: 'info' });
        }}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </>
  );
};

export default RefineArtworkModal; 